import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import Honeypot from "@/components/Honeypot";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Disc3, Check, Loader2, Star, ArrowRight, Upload, ExternalLink, Image as ImageIcon } from "lucide-react";

const inputCls =
  "w-full bg-ink border border-stroke px-4 py-3 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid transition-colors";
const adImg = (p) => `${API}/advertising/image/${p}`;

export default function Advertise() {
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({ advertiser: "", brand: "", email: "", headline: "", link_url: "", tier: "" });
  const [imagePath, setImagePath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [loadedAtMs] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { pay_url, amount }
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/advertising/tiers").then((r) => setTiers(r.data)).catch(() => setTiers([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pick = (tierId) => {
    set("tier", tierId);
    document.getElementById("advertise-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/advertising/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImagePath(data.path);
      toast.success("Banner uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.advertiser.trim() || !form.email.trim()) return toast.error("Name and email are required");
    if (!form.tier) return toast.error("Pick an advertising package");
    if (!form.link_url.trim()) return toast.error("Add a click-through link");
    if (!imagePath) return toast.error("Upload a banner image");
    setSubmitting(true);
    try {
      const { data } = await api.post("/advertising/inquiries", {
        ...form, image_path: imagePath, website, loaded_at_ms: loadedAtMs,
      });
      setDone({ pay_url: data.pay_url, amount: data.amount });
      toast.success("Ad request sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDone(null);
    setForm({ advertiser: "", brand: "", email: "", headline: "", link_url: "", tier: "" });
    setImagePath("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="relative z-20 flex items-center justify-between px-6 lg:px-12 py-5 border-b border-stroke bg-surface/70 backdrop-blur-sm">
        <Link to="/" className="flex items-center gap-2">
          <Disc3 className="text-acid" size={28} />
          <span className="font-display text-3xl text-paper">POSTERVAULT</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/sponsorship" data-testid="advertise-sponsorship-link" className="font-mono text-xs uppercase tracking-wider text-muted-gray hover:text-paper px-3 py-2 hidden sm:block">
            Sponsorship
          </Link>
          <Link to="/register" className="font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-4 py-2.5 hard-shadow">
            Start Collecting
          </Link>
        </div>
      </header>

      <section className="relative z-10 px-6 lg:px-12 py-16 flex-1">
        <div className="max-w-6xl mx-auto">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid mb-4">Advertise With Us</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-paper leading-[0.9] ink-offset">
            BANNER<br />ADVERTISING
          </h1>
          <p className="mt-5 font-sans text-base text-muted-gray max-w-xl leading-relaxed">
            Put your banner in front of serious poster collectors. Buy a package, send your artwork, and your
            banner rotates across the landing page, the app and public galleries for the full term.
          </p>

          <div className="grid md:grid-cols-3 gap-5 mt-12" data-testid="ad-tiers">
            {tiers.map((t) => (
              <div
                key={t.id}
                data-testid={`ad-tier-${t.id}`}
                className={`relative bg-surface border p-7 hard-shadow flex flex-col ${t.popular ? "border-acid" : "border-stroke"}`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-7 font-mono text-[10px] uppercase tracking-widest bg-acid text-ink font-bold px-2.5 py-1 flex items-center gap-1">
                    <Star size={11} /> Most Popular
                  </span>
                )}
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-gray">{t.tagline}</div>
                <div className="font-display text-4xl text-paper mt-1">{t.name}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-5xl text-acid">${t.price}</span>
                  <span className="font-mono text-xs text-muted-gray">/ {t.months} {t.months === 1 ? "month" : "months"}</span>
                </div>
                <div className="font-mono text-[11px] text-muted-gray mt-1">
                  ≈ ${(t.price / t.months).toFixed(2)} per month
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 font-sans text-sm text-paper/90">
                      <Check size={16} className="text-acid shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => pick(t.id)}
                  data-testid={`ad-select-${t.id}`}
                  className={`mt-7 w-full font-mono text-xs uppercase tracking-wider py-3.5 font-bold flex items-center justify-center gap-2 ${
                    t.popular ? "bg-acid text-ink hover:bg-glow" : "border border-stroke text-paper hover:border-acid hover:text-acid"
                  }`}
                >
                  Buy {t.name} <ArrowRight size={15} />
                </button>
              </div>
            ))}
          </div>

          {/* Buy / inquiry form */}
          <div id="advertise-form" className="mt-16 max-w-2xl">
            <h2 className="font-display text-4xl text-paper">Place Your Banner</h2>
            <p className="font-mono text-xs text-muted-gray mt-2 mb-6">
              Upload your banner and details. Pay online, then we activate your ad.
            </p>

            {done ? (
              <div className="bg-surface border border-acid/40 p-8 text-center" data-testid="advertise-success">
                <Check className="text-acid mx-auto mb-3" size={32} />
                <div className="font-display text-3xl text-paper">Ad request received!</div>
                <p className="font-mono text-xs text-muted-gray mt-2">
                  Complete payment below and we'll put your banner live.
                </p>
                {done.pay_url ? (
                  <a href={done.pay_url} target="_blank" rel="noopener noreferrer" data-testid="advertise-pay-link"
                    className="mt-5 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider bg-acid text-ink font-bold px-6 py-3.5 hover:bg-glow">
                    <ExternalLink size={16} /> Pay ${done.amount} Now
                  </a>
                ) : (
                  <p className="mt-4 font-mono text-xs text-gold">We'll email you a payment link shortly.</p>
                )}
                <div className="mt-5">
                  <button onClick={reset} className="font-mono text-xs uppercase text-muted-gray hover:text-acid hover:underline">
                    Submit another ad
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-surface/85 backdrop-blur-md border border-stroke p-7 space-y-4" data-testid="advertise-form">
                <Honeypot value={website} onChange={setWebsite} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="ad-advertiser-input" required placeholder="YOUR NAME" className={inputCls}
                    value={form.advertiser} onChange={(e) => set("advertiser", e.target.value)} />
                  <input data-testid="ad-brand-input" placeholder="BRAND / COMPANY" className={inputCls}
                    value={form.brand} onChange={(e) => set("brand", e.target.value)} />
                </div>
                <input data-testid="ad-email-input" type="email" required placeholder="EMAIL" className={inputCls}
                  value={form.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="ad-headline-input" placeholder="HEADLINE (e.g. Framing for collectors)" className={inputCls}
                  value={form.headline} onChange={(e) => set("headline", e.target.value)} />
                <input data-testid="ad-link-input" required placeholder="CLICK-THROUGH LINK (https://...)" className={inputCls}
                  value={form.link_url} onChange={(e) => set("link_url", e.target.value)} />
                <select data-testid="ad-tier-select" required className={inputCls}
                  value={form.tier} onChange={(e) => set("tier", e.target.value)}>
                  <option value="">Select a package</option>
                  {tiers.map((t) => <option key={t.id} value={t.id}>{t.name} — ${t.price} / {t.months}mo</option>)}
                </select>

                {/* Banner upload */}
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-2">Banner image</div>
                  {imagePath ? (
                    <div className="flex items-center gap-3 border border-acid/40 p-2 bg-ink">
                      <img src={adImg(imagePath)} alt="banner preview" className="h-16 w-28 object-cover border border-stroke" />
                      <span className="font-mono text-xs text-acid flex-1">Uploaded</span>
                      <button type="button" onClick={() => { setImagePath(""); if (fileRef.current) fileRef.current.value = ""; }}
                        className="font-mono text-[10px] uppercase text-muted-gray hover:text-magenta">Replace</button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 border border-dashed border-stroke py-6 cursor-pointer hover:border-acid text-muted-gray hover:text-acid">
                      {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                      <span className="font-mono text-xs uppercase tracking-wider">{uploading ? "Uploading..." : "Upload banner (jpg/png, max 8MB)"}</span>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="ad-image-input" />
                    </label>
                  )}
                </div>

                <button type="submit" disabled={submitting || uploading} data-testid="advertise-submit-button"
                  className="w-full bg-acid text-ink font-bold font-mono text-sm uppercase tracking-wider py-3.5 hover:bg-glow disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting && <Loader2 size={16} className="animate-spin" />} <ImageIcon size={15} /> Submit & Pay
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
